import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard, SectionCard, ChartEmpty } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CHART } from "@/lib/helpers";
import { ITSupportDialog } from "@/components/ITSupportDialog";
import { FaceEnrolmentNudge } from "@/components/FaceEnrolmentNudge";
import {
  Users, UserCheck, UserX, ClipboardList, CheckCircle2, Clock, Percent,
  TrendingUp, Target, Timer, Trophy, AlertTriangle, LogIn, ShieldCheck, RefreshCw, Trash2, LifeBuoy,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  Legend, LineChart, Line, CartesianGrid,
} from "recharts";

export default function ExecutiveDashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["executive"],
    queryFn: async () => (await api.get("/dashboard/executive")).data,
    refetchInterval: 30000,
  });

  const handleReset = async () => {
    try {
      const res = await api.post("/admin/reset");
      const d = res.data.deleted;
      const total = Object.values(d).reduce((a, b) => a + b, 0);
      ["executive", "performance", "management", "tasks", "attendance", "quality", "worklogs", "leaves"].forEach((key) =>
        qc.invalidateQueries({ queryKey: [key] })
      );
      toast.success(`Data reset — ${total} records cleared. Employees kept.`);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Only the Founder can reset data" : "Reset failed");
    }
  };

  if (isLoading)
    return (
      <div>
        <PageHeader title="Executive Dashboard" subtitle="Live operational KPIs" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-lg" />)}
        </div>
      </div>
    );

  const k = data.kpi;
  const c = data.charts;
  const l = data.live || {};
  const taskColors = [CHART.green, CHART.amber, CHART.red, CHART.darkred];
  const attColors = [CHART.green, CHART.red];
  const hasPerf = c.performance?.length > 0;
  const hasAttendance = c.attendance_pie?.some((x) => x.value > 0);
  const hasTaskStatus = c.task_status?.some((x) => x.value > 0);
  const hasTrend = c.trend?.some((x) => x.present > 0 || x.accuracy > 0 || x.hours > 0);
  const hasThroughput = c.throughput?.some((x) => x.completed > 0);
  const hasQualityTrend = c.quality_trend?.some((x) => x.quality_score > 0 || x.accuracy > 0);

  return (
    <div data-testid="executive-dashboard">
      <FaceEnrolmentNudge />
      <PageHeader title="Executive Dashboard" subtitle="Live operational KPIs · auto-refreshes every 30s">
        <ITSupportDialog trigger={
          <Button variant="outline" data-testid="dashboard-contact-it-btn" className="rounded-lg border-navy text-navy hover:bg-navy/5">
            <LifeBuoy className="mr-2 h-4 w-4" /> Contact IT Team
          </Button>
        } />
        {user?.role === "founder" && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="reset-data-btn" className="rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                <Trash2 className="mr-2 h-4 w-4" /> Reset Data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading text-navy">Reset all operational data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes all tasks, attendance, quality checks, work logs and leave
                  requests. The 7 employees and company holidays are kept. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleReset} data-testid="reset-confirm-btn" className="bg-red-600 hover:bg-red-700 text-white">
                  Yes, reset everything
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        <Button
          onClick={() => refetch()}
          data-testid="refresh-dashboard-btn"
          className="bg-navy hover:bg-navy-900 text-white rounded-lg"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard testId="kpi-total-employees" label="Total Employees" value={k.total_employees} icon={Users} />
        <KpiCard testId="kpi-present" label="Present Today" value={k.present_today} icon={UserCheck} accent="green" />
        <KpiCard testId="kpi-absent" label="Absent Today" value={k.absent_today} icon={UserX} accent="red" />
        <KpiCard testId="kpi-late" label="Late Logins" value={k.late_login_count} icon={LogIn} accent="amber" />
        <KpiCard testId="kpi-assigned" label="Tasks Assigned" value={k.tasks_assigned} icon={ClipboardList} />
        <KpiCard testId="kpi-completed" label="Completed" value={k.completed} icon={CheckCircle2} accent="green" />
        <KpiCard testId="kpi-pending" label="Pending" value={k.pending} icon={Clock} accent="red" />
        <KpiCard testId="kpi-completion" label="Completion %" value={`${k.completion_pct}%`} icon={Percent} accent="gold" />
        <KpiCard testId="kpi-productivity" label="Productivity %" value={`${k.productivity_pct}%`} icon={TrendingUp} accent="gold" />
        <KpiCard testId="kpi-accuracy" label="Accuracy %" value={`${k.accuracy_pct}%`} icon={Target} accent="green" />
        <KpiCard testId="kpi-hours" label="Working Hours" value={k.total_working_hours} sub="today" icon={Timer} />
        <KpiCard testId="kpi-quality" label="Quality Score" value={k.quality_score} icon={ShieldCheck} accent="gold" />
        <KpiCard testId="kpi-top" label="Top Performer" value={k.top_performer} icon={Trophy} accent="gold" />
        <KpiCard testId="kpi-attention" label="Need Attention" value={k.attention_count} icon={AlertTriangle} accent="red" />
      </div>

      {/* --- Live Operations --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard
          title="Punched In Right Now"
          testId="live-punched-in"
          action={<span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600">{l.punched_in_count || 0} active</span>}
        >
          {l.punched_in?.length ? (
            <ul className="divide-y divide-slate-100" data-testid="punched-in-list">
              {l.punched_in.map((p) => (
                <li key={p.employee_id} className="flex items-center justify-between py-2.5" data-testid={`punched-in-${p.employee_id}`}>
                  <div className="flex items-center gap-2.5">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                    </span>
                    <span className="text-sm font-medium text-navy">{p.name}</span>
                  </div>
                  <span className="text-xs font-semibold text-slate-500">{p.hours}h today</span>
                </li>
              ))}
            </ul>
          ) : (
            <ChartEmpty icon={UserCheck} message="Nobody is punched in right now" height={180} />
          )}
        </SectionCard>

        <SectionCard
          title="On Leave / Absent Today"
          testId="live-absent"
          action={<span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600">{l.absent_count || 0}</span>}
        >
          {l.absent_roster?.length ? (
            <ul className="divide-y divide-slate-100 max-h-52 overflow-y-auto" data-testid="absent-list">
              {l.absent_roster.map((p) => (
                <li key={p.employee_id} className="flex items-center justify-between py-2.5" data-testid={`absent-${p.employee_id}`}>
                  <span className="text-sm font-medium text-navy">{p.name}</span>
                  <span className={`text-xs font-semibold ${p.status === "On Leave" ? "text-amber-600" : "text-slate-400"}`}>{p.status}</span>
                </li>
              ))}
            </ul>
          ) : (
            <ChartEmpty icon={UserCheck} message="Everyone is present today" height={180} />
          )}
        </SectionCard>

        <div className="grid grid-cols-1 gap-6">
          <SectionCard title="Tasks Due Today" testId="live-due-today">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-amber-50 p-3"><Clock className="h-7 w-7 text-amber-600" /></div>
              <div>
                <p className="text-4xl font-heading font-extrabold text-navy" data-testid="due-today-count">{l.due_today ?? 0}</p>
                <p className="text-xs text-slate-500">not yet completed</p>
              </div>
            </div>
          </SectionCard>
          <SectionCard title="Overdue Tasks" testId="live-overdue">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-red-50 p-3"><AlertTriangle className="h-7 w-7 text-red-600" /></div>
              <div>
                <p className="text-4xl font-heading font-extrabold text-red-600" data-testid="overdue-count">{l.overdue ?? 0}</p>
                <p className="text-xs text-slate-500">past deadline</p>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      {/* --- Top & Bottom Performers --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="Top Performer This Month" testId="live-top-performer">
          {l.top ? (
            <div className="flex items-center gap-4" data-testid="top-performer-card">
              <div className="rounded-full bg-gold/10 p-3.5"><Trophy className="h-8 w-8 text-gold-dark" /></div>
              <div className="flex-1">
                <p className="text-lg font-heading font-bold text-navy">{l.top.name}</p>
                <p className="text-xs text-slate-500">Overall score</p>
              </div>
              <span className="rounded-lg bg-emerald-50 px-3 py-1.5 text-xl font-heading font-extrabold text-emerald-600">{l.top.overall}%</span>
            </div>
          ) : <ChartEmpty icon={Trophy} message="No performance data yet" height={120} />}
        </SectionCard>
        <SectionCard title="Needs Attention This Month" testId="live-bottom-performer">
          {l.bottom ? (
            <div className="flex items-center gap-4" data-testid="bottom-performer-card">
              <div className="rounded-full bg-red-50 p-3.5"><AlertTriangle className="h-8 w-8 text-red-600" /></div>
              <div className="flex-1">
                <p className="text-lg font-heading font-bold text-navy">{l.bottom.name}</p>
                <p className="text-xs text-slate-500">Overall score</p>
              </div>
              <span className="rounded-lg bg-red-50 px-3 py-1.5 text-xl font-heading font-extrabold text-red-600">{l.bottom.overall}%</span>
            </div>
          ) : <ChartEmpty icon={AlertTriangle} message="No performance data yet" height={120} />}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Performance by Employee" className="lg:col-span-2" testId="chart-performance">
          {hasPerf ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={c.performance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="completion" name="Completion %" fill={CHART.navy} radius={[4, 4, 0, 0]} />
              <Bar dataKey="accuracy" name="Accuracy %" fill={CHART.gold} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          ) : <ChartEmpty icon={TrendingUp} message="No performance data yet" height={300} />}
        </SectionCard>

        <SectionCard title="Attendance Today" testId="chart-attendance">
          {hasAttendance ? (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={c.attendance_pie} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={3}>
                {c.attendance_pie.map((e, i) => <Cell key={i} fill={attColors[i]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          ) : <ChartEmpty icon={UserCheck} message="No attendance marked today" height={300} />}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Task Status Distribution" testId="chart-task-status">
          {hasTaskStatus ? (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={c.task_status} dataKey="value" nameKey="name" outerRadius={95} label>
                {c.task_status.map((e, i) => <Cell key={i} fill={taskColors[i]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          ) : <ChartEmpty icon={ClipboardList} message="No tasks created yet" />}
        </SectionCard>

        <SectionCard title="7-Day Trend" className="lg:col-span-2" testId="chart-trend">
          {hasTrend ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={c.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="present" name="Present" stroke={CHART.navy} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="accuracy" name="Accuracy %" stroke={CHART.gold} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="hours" name="Hours" stroke={CHART.green} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
          ) : <ChartEmpty icon={Timer} message="No activity in the last 7 days" />}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="Task Throughput (tasks completed / day)" testId="chart-throughput">
          {hasThroughput ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={c.throughput}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
              <Tooltip />
              <Bar dataKey="completed" name="Completed" fill={CHART.green} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          ) : <ChartEmpty icon={CheckCircle2} message="No tasks completed in the last 7 days" />}
        </SectionCard>

        <SectionCard title="Quality Score Trend" testId="chart-quality-trend">
          {hasQualityTrend ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={c.quality_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#64748b" }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="quality_score" name="Quality Score" stroke={CHART.gold} strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="accuracy" name="Accuracy %" stroke={CHART.navy} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
          ) : <ChartEmpty icon={ShieldCheck} message="No quality checks in the last 7 days" />}
        </SectionCard>
      </div>
    </div>
  );
}
